import React, { useMemo } from 'react';
import { FiPackage, FiClock, FiTruck, FiDollarSign, FiCheckCircle, FiAlertCircle, FiAlertTriangle } from 'react-icons/fi';
import {
  isAssignDriverOrder,
  isDoorDeliveryDispatched,
  isDoorDeliveryOrder,
  needsDispatchAttention,
  needsDispatchDeliveryTime,
  needsDispatchEntryInstructions,
} from '../utils/dispatchOrderBuckets';

// Get today's date in PST
const getTodayInPST = () => {
  const now = new Date();
  const pstString = now.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  return pstString; // Returns YYYY-MM-DD format
};

// Check if a date is today in PST
const isCreatedTodayPST = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const datePST = date.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  return datePST === getTodayInPST();
};

// Calculate total order revenue (all fees)
const calculateOrderRevenue = (order) => {
  const driverFee = parseFloat(order.driverFee) || 0;
  const groceryFee = parseFloat(order.groceryFee) || 0;
  const hostFee = parseFloat(order.hostFee) || 0;
  const tip = parseFloat(order.tip || order.Tip) || 0;
  const bellhoptFee = parseFloat(order.bellhoptFee) || 0;
  const alcoholFee = parseFloat(order.alcoholFee) || 0;
  return driverFee + groceryFee + hostFee + tip + bellhoptFee + alcoholFee;
};

// Check if an order is fully dispatched (same logic as DispatchScreen).
// Door-delivery orders use the door-aware helper so they don't fall back
// into Awaiting Dispatch on refresh — see `isDoorDeliveryDispatched`.
const isDispatched = (order) => {
  if (order.status === 'completed') return false;
  if (isDoorDeliveryOrder(order)) return isDoorDeliveryDispatched(order);
  const hasDriver = !!order.driverID;
  const spend = parseFloat(order.storeSpend);
  const hasValidSpend = !isNaN(spend) && spend > 0;
  const hasOrderId = order.storeOrderId?.trim().length > 0 || order.storeOrderConfirmationUrl?.trim().length > 0;
  return hasDriver && hasValidSpend && hasOrderId;
};

export default function PerformanceMetrics({ orders, drivers, completedOrders }) {
  const metrics = useMemo(() => {
    // Filter orders created today (using createdAt in PST)
    const ordersCreatedToday = [...orders, ...completedOrders].filter(o => 
      isCreatedTodayPST(o.createdAt)
    );
    
    const completedToday = completedOrders.filter(o => 
      isCreatedTodayPST(o.createdAt) && o.status === 'completed'
    );

    const activeDrivers = new Set(orders.filter(o => o.driverID).map(o => o.driverID)).size;
    const totalDrivers = drivers.length;
    
    // Revenue: sum of all fees for orders created today
    const revenue = ordersCreatedToday.reduce((sum, o) => sum + calculateOrderRevenue(o), 0);
    
    // ─── Categorize orders like the tabs do ─────────────────────────
    
    // Awaiting Dispatch (Tab 0): paymentCompleted but no store order placed.
    // Excludes already-dispatched door-delivery orders so they don't bounce
    // back into the awaiting bucket on refresh.
    const awaitingDispatch = orders.filter(o => {
      if (isDoorDeliveryDispatched(o)) return false;
      const hasStoreOrderId = o.storeOrderId?.trim().length > 0 || o.storeOrderConfirmationUrl?.trim().length > 0;
      const hasStoreSpend = parseFloat(o.storeSpend) > 0;
      return o.status === 'paymentCompleted' && (!hasStoreSpend || !hasStoreOrderId);
    });

    // Awaiting Driver Pay (Tab 1): paymentCompleted with store order placed
    const awaitingDriverPay = orders.filter(o => {
      const hasStoreOrderId = o.storeOrderId?.trim().length > 0 || o.storeOrderConfirmationUrl?.trim().length > 0;
      const hasStoreSpend = parseFloat(o.storeSpend) > 0;
      return o.status === 'paymentCompleted' && hasStoreSpend && hasStoreOrderId;
    });

    // Assign Driver (Tab 2): pending/requested, store placed, driver pay set, no driver on parent
    const assignDriver = orders.filter((o) => isAssignDriverOrder(o, orders));

    // Dispatched orders (don't show issues for these)
    const dispatchedCount = orders.filter(o => isDispatched(o)).length;

    // Missing Details: orders flagged with needsEntryInstructions or needsDeliveryTime
    // Only count non-dispatched orders
    const ordersWithMissingDetails = orders.filter(o => {
      if (isDispatched(o)) return false; // Don't show for dispatched
      return needsDispatchAttention(o);
    });

    return {
      totalActive: orders.length,
      completedToday: completedToday.length,
      activeDrivers,
      totalDrivers,
      revenue: revenue.toFixed(0),
      // Tab-based counts
      needsPlacement: awaitingDispatch.length,
      needsPaySet: awaitingDriverPay.length,
      needsDriver: assignDriver.length,
      needsDetails: ordersWithMissingDetails.length,
      ordersWithMissingDetails, // For tooltip/detail
    };
  }, [orders, drivers, completedOrders]);

  return (
    <div className="flex items-center gap-6 text-sm flex-wrap">
      {/* Total Active Orders */}
      <div className="flex items-center gap-2">
        <FiPackage className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        <span className="text-gray-600 dark:text-gray-400">Active:</span>
        <span className="font-semibold text-gray-900 dark:text-white">{metrics.totalActive}</span>
      </div>

      <div className="h-4 w-px bg-gray-200 dark:bg-gray-700" />

      {/* Completed Today */}
      <div className="flex items-center gap-2">
        <FiCheckCircle className="w-4 h-4 text-emerald-500" />
        <span className="text-gray-600 dark:text-gray-400">Completed:</span>
        <span className="font-semibold text-gray-900 dark:text-white">{metrics.completedToday}</span>
        <span className="text-gray-400 dark:text-gray-500">today</span>
      </div>

      <div className="h-4 w-px bg-gray-200 dark:bg-gray-700" />

      {/* Active Drivers */}
      <div className="flex items-center gap-2">
        <FiTruck className="w-4 h-4 text-sky-500" />
        <span className="text-gray-600 dark:text-gray-400">Concierges:</span>
        <span className="font-semibold text-gray-900 dark:text-white">
          {metrics.activeDrivers}/{metrics.totalDrivers}
        </span>
      </div>

      <div className="h-4 w-px bg-gray-200 dark:bg-gray-700" />

      {/* Revenue Today */}
      <div className="flex items-center gap-2">
        <FiDollarSign className="w-4 h-4 text-emerald-500" />
        <span className="text-gray-600 dark:text-gray-400">Today:</span>
        <span className="font-semibold text-gray-900 dark:text-white">${metrics.revenue}</span>
      </div>

      {/* Separator for problem stats */}
      <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />

      {/* Needs Placement - Awaiting Dispatch tab */}
      {metrics.needsPlacement > 0 && (
        <div className="flex items-center gap-2 px-2 py-1 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <FiAlertCircle className="w-4 h-4 text-red-500" />
          <span className="font-semibold text-red-600 dark:text-red-400">{metrics.needsPlacement}</span>
          <span className="text-red-600 dark:text-red-400">need placement</span>
        </div>
      )}

      {/* Needs Pay Set - Awaiting Driver Pay tab */}
      {metrics.needsPaySet > 0 && (
        <div className="flex items-center gap-2 px-2 py-1 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <FiDollarSign className="w-4 h-4 text-amber-500" />
          <span className="font-semibold text-amber-600 dark:text-amber-400">{metrics.needsPaySet}</span>
          <span className="text-amber-600 dark:text-amber-400">need pay set</span>
        </div>
      )}

      {/* Needs Driver - Assign Driver tab */}
      {metrics.needsDriver > 0 && (
        <div className="flex items-center gap-2 px-2 py-1 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <FiTruck className="w-4 h-4 text-orange-500" />
          <span className="font-semibold text-orange-600 dark:text-orange-400">{metrics.needsDriver}</span>
          <span className="text-orange-600 dark:text-orange-400">need concierge</span>
        </div>
      )}

      {/* Missing Details - Entry/Time flags (not dispatched) */}
      {metrics.needsDetails > 0 && (
        <div 
          className="flex items-center gap-2 px-2 py-1 bg-violet-50 dark:bg-violet-900/20 rounded-lg cursor-help"
          title={metrics.ordersWithMissingDetails.map(o => {
            const missing = [];
            if (needsDispatchEntryInstructions(o)) missing.push('entry');
            if (needsDispatchDeliveryTime(o)) missing.push('time');
            return `#${o.uniqueId}: needs ${missing.join(' & ')}`;
          }).join('\n')}
        >
          <FiAlertTriangle className="w-4 h-4 text-violet-500" />
          <span className="font-semibold text-violet-600 dark:text-violet-400">{metrics.needsDetails}</span>
          <span className="text-violet-600 dark:text-violet-400">missing details</span>
        </div>
      )}
    </div>
  );
}
